<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { availabilityColor } from "./availabilityColor";
import FillRateBar from "./FillRateBar.vue";
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const props = defineProps<{
	clusterId?: number;
	count: number;
	map?: any;
	sourceId?: string;
	coordinates: [number, number];
	screens: ScreenDO[];
	getPopup: () => any;
}>();

const nodeIndex = ref(0);
const resolvedScreens = ref<ScreenDO[]>([]);

onMounted(() => {
	if (props.clusterId !== undefined && props.map && props.sourceId) {
		props.map
			.getSource(props.sourceId)
			.getClusterLeaves(
				props.clusterId,
				Infinity,
				0,
				(err: any, features: any[]) => {
					if (!err) {
						const ids = features
							.map((f) => f.properties?.id as number)
							.filter(Boolean);
						resolvedScreens.value = ids
							.map((id) => props.screens.find((s) => s.id === id))
							.filter((s): s is ScreenDO => !!s);
					}
				}
			);
	} else {
		resolvedScreens.value = props.screens;
	}
});

const current = computed(() => resolvedScreens.value[nodeIndex.value] ?? null);

const fillRate = computed(() => {
	const rate = current.value?.availability?.fill_rate;
	return rate != null ? rate * 100 : null;
});

const averageFillRate = computed(() => {
	const withData = resolvedScreens.value.filter((s) => s.availability?.fill_rate != null);
	if (withData.length === 0) return null;
	return (withData.reduce((sum, s) => sum + s.availability!.fill_rate!, 0) / withData.length) * 100;
});

const zoomIn = () => {
	props.map
		.getSource(props.sourceId)
		.getClusterExpansionZoom(props.clusterId, (err: any, zoom: number) => {
			if (err) return;
			props.map.easeTo({ center: props.coordinates, zoom });
		});
	props.getPopup()?.remove();
};
</script>

<template>
	<div class="fm-popup">
		<div class="fm-popup__header">
			<span class="fm-popup__title">
				{{ count }} Screen{{ count !== 1 ? "s" : "" }}
				<span v-if="averageFillRate !== null">
					· {{ averageFillRate.toFixed(1) }}% Fill rate
				</span>
			</span>
			<div v-if="resolvedScreens.length > 1" class="fm-popup__pager">
				<button
					class="fm-popup__pager-btn"
					:disabled="nodeIndex <= 0"
					@click="nodeIndex--"
				>
					&#8249;
				</button>
				<span class="fm-popup__pager-label">
					{{ nodeIndex + 1 }}/{{ resolvedScreens.length }}
				</span>
				<button
					class="fm-popup__pager-btn"
					:disabled="nodeIndex >= resolvedScreens.length - 1"
					@click="nodeIndex++"
				>
					&#8250;
				</button>
			</div>
		</div>

		<div class="fm-popup__divider" />

		<template v-if="current">
			<div class="fm-popup__name fm-tip" :data-tip="current.name ?? '—'">
				{{ current.name ?? "—" }}
				<v-tooltip activator="parent">
					{{ current.name ?? "—" }}
				</v-tooltip>
			</div>
			<div
				v-if="current.reference_id"
				class="fm-popup__meta fm-tip"
				:data-tip="current.reference_id"
			>
				{{ current.reference_id }}
			</div>
			<div v-if="current.size?.label" class="fm-popup__meta">
				{{ current.size.label }}
			</div>
			<fill-rate-bar :fill-rate="fillRate" />
		</template>
		<div v-else class="fm-popup__meta">Loading screens…</div>

		<template v-if="clusterId !== undefined">
			<div class="fm-popup__divider" />
			<button class="fm-popup__zoom-btn" @click="zoomIn">Zoom in</button>
		</template>
	</div>
</template>

<style scoped>
.fm-popup {
	width: 300px;
	color: #fff;
}
.fm-popup__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	min-width: 0;
}
.fm-popup__title {
	font-size: 0.75rem;
	opacity: 0.65;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.fm-popup__title-avg {
	font-weight: 600;
	opacity: 1;
}
.fm-popup__name {
	font-size: 0.875rem;
	font-weight: 600;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.fm-popup__meta {
	font-size: 0.75rem;
	opacity: 0.65;
	margin-top: 2px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.fm-popup__divider {
	height: 1px;
	background: rgba(255, 255, 255, 0.15);
	margin: 8px 0;
}

.fm-popup__pager {
	display: flex;
	align-items: center;
	gap: 4px;
}
.fm-popup__pager-btn {
	background: rgba(255, 255, 255, 0.15);
	border: none;
	color: #fff;
	border-radius: 4px;
	width: 22px;
	height: 22px;
	font-size: 1rem;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
}
.fm-popup__pager-btn:disabled {
	opacity: 0.3;
	cursor: default;
}
.fm-popup__pager-label {
	font-size: 0.75rem;
	opacity: 0.65;
	min-width: 32px;
	text-align: center;
}
.fm-popup__zoom-btn {
	width: 100%;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.2);
	color: #fff;
	border-radius: 6px;
	padding: 5px 0;
	font-size: 0.8rem;
	cursor: pointer;
	transition: background 0.15s;
}
.fm-popup__zoom-btn:hover {
	background: rgba(255, 255, 255, 0.2);
}
</style>
